import "./TestChat.css";
import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  MedievalBackground,
  MedievalLocations,
  MedievalWorld,
  MedievalIntroduction,
} from "../scenarios/MedievalScenario";
import { ReactComponent as Logo } from "../assets/Logo.svg";
import { useIsMobile } from "../hooks/useIsMobile";
import languages from "../assets/languages.json";
import { useAuthContext } from "../firebase/AuthContext";
import Backpack from "../assets/backpack_icon.webp";

function TestChat() {
  const [text, setText] = useState("");
  const [introduction, setIntroduction] = useState(() => (e) => {});
  const [introMessage, setIntroMessage] = useState("");
  const [generating, setGenerating] = useState(false);
  const [messages, setMessages] = useState([]);
  const [currentSummary, setCurrentSummary] = useState(null);
  const [error, setError] = useState("");
  const [journeyStarted, setJourneyStarted] = useState(false);
  const [showInventory, setShowInventory] = useState(false);

  const [scenario, setScenario] = useState(() => () => {});
  const [background, setBackground] = useState(null);
  const [backgroundCover, setBackgroundCover] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relations, setRelations] = useState({});
  const [items, setItems] = useState({});
  const [currentLocation, setCurrentLocation] = useState("");
  const [world, setWorld] = useState("");
  const [locations, setLocations] = useState([]);
  const [languageIndex, setLanguageIndex] = useState(0);
  const messagesRef = useRef(null);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { user } = useAuthContext();

  if (user) {
    navigate("/", { replace: true });
  }

  const texts = {
    en: {
      startJourney: "Start your Journey!",
      inputFieldHint: "What are you going to do?...",
      expired: "Your free trial has ended!",
      freeTrialMessage: "free trial",
    },
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      setGenerating(true);
      event.preventDefault();
      const prompt = {
        role: "user",
        content: `${text}. *Answer in JSON* *Check inventory* *Answer in ${languages.languages[languageIndex].name}*`,
      };

      console.log(`Background: ${background}`);
      console.log(`Background Cover: ${backgroundCover}`);

      if (backgroundCover != background) {
        setBackgroundCover(background);
      }

      if (currentSummary) {
        sendMessage([
          { role: "assistant", content: currentSummary["text"] ?? "" },
          ...messages.slice(currentSummary["index"]),
          prompt,
        ]);
      } else {
        sendMessage([...messages, prompt]);
      }

      setText("");
    }
  };

  useEffect(() => {
    setLoading(true);
    setScenario(() => (e, c) => setBackground(MedievalBackground(e, c)));
    setBackground(MedievalBackground());
    setBackgroundCover(MedievalBackground());
    setLocations(MedievalLocations());
    setCurrentLocation(MedievalLocations()[0]);
    setWorld(MedievalWorld());
    setIntroduction(() => (e) => setIntroMessage(MedievalIntroduction(e)));
    setIntroMessage(MedievalIntroduction(languages.languages[0].short));
    setLoading(false);
  }, []);

  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = (prompt, giveOptions = false) => {
    if (error) setError("");

    if (messages.length > 12) {
      return;
    }

    fetch("https://prompttomodel-ihg7hu5nyq-ew.a.run.app", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        prompt: prompt,
        items: items,
        relations: relations,
        currentLocation: currentLocation,
        world: world,
        locations: locations,
        freeTrial: messages.length <= 12,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        const output = data["output"];
        const summary = data["summary"];
        let extractedJson = null;

        try {
          extractedJson = JSON.parse(output);
        } catch {
          const jsonMatch = output.match(/({.*})/s);
          if (jsonMatch) {
            try {
              extractedJson = JSON.parse(jsonMatch[1]);
            } catch {
              console.log("JSON is invalid.");
            }
          }
        }

        let summaryJson = summary;
        if (summary) {
          try {
            const parsedSummary = JSON.parse(summary);
            summaryJson =
              parsedSummary["story"] ?? Object.values(parsedSummary)[0];
          } catch (error) {
            const jsonMatch = summary.match(/({.*})/s);
            if (jsonMatch) {
              try {
                const parsedSummary = JSON.parse(jsonMatch[1]);
                summaryJson =
                  parsedSummary["story"] ?? Object.values(parsedSummary)[0];
              } catch (error) {
                console.log(`error: ${error}`);
              }
            }
          }

          setCurrentSummary({ text: summaryJson, index: messages.length });
        }

        if (extractedJson) {
          console.log(extractedJson);
          if (extractedJson["story"]) {
            if (!giveOptions) {
              setMessages([
                ...messages,
                { role: "user", content: text },
                { role: "assistant", content: extractedJson["story"] },
              ]);
            } else {
              setMessages([
                ...messages,
                { role: "user", content: "" },
                { role: "assistant", content: extractedJson["story"] },
              ]);
            }
            scenario(extractedJson["location"], background);
            setCurrentLocation(extractedJson["location"]);
            setRelations(extractedJson["relations"]);
            setItems(extractedJson["items"]);
          } else {
            const firstKeyValue = Object.entries(extractedJson)[0];

            if (!giveOptions) {
              setMessages([
                ...messages,
                { role: "user", content: text },
                {
                  role: "assistant",
                  content: JSON.stringify(firstKeyValue[1]),
                },
              ]);
            } else {
              setMessages([
                ...messages,
                { role: "user", content: "" },
                {
                  role: "assistant",
                  content: JSON.stringify(firstKeyValue[1]),
                },
              ]);
            }
          }
        } else {
          setError(data);
        }
        setGenerating(false);
      })
      .catch((e) => {
        console.log(e);
        setGenerating(false);
      });
  };

  const startJourney = async () => {
    setJourneyStarted(true);
    setGenerating(true);

    const startPrompt = `Start the story! *Answer in ${languages.languages[languageIndex].name}* *Answer in JSON*`;

    sendMessage([
      { role: "assistant", content: introMessage },
      { role: "user", content: startPrompt },
    ]);
  };

  const askForOptions = () => {
    setGenerating(true);
    const prompt = {
      role: "user",
      content: `What are my options? *Answer in JSON* *Check inventory* *Answer in ${languages.languages[languageIndex].name}*`,
    };

    if (currentSummary) {
      sendMessage(
        [
          { role: "assistant", content: currentSummary["text"] ?? "" },
          ...messages.slice(currentSummary["index"]),
          prompt,
        ],
        true
      );
    } else {
      sendMessage([...messages, prompt], true);
    }
  };

  return (
    <div className="free-trial">
      {!loading ? (
        <>
          <div
            className="background"
            style={{
              backgroundImage: `url(${backgroundCover})`,
              transition: "all 2s",
            }}
          ></div>
          <div
            className="background"
            style={{
              backgroundImage: `url(${background})`,
              backgroundColor: "rgba(0,0,0,0)",
              transition: "all 2s",
              zIndex: -98,
            }}
          ></div>

          {!journeyStarted && (
            <div className="free-trial-message">
              {texts["en"]["freeTrialMessage"]}
            </div>
          )}
          <div className="messages" ref={messagesRef}>
            {messages.length > 0 ? (
              messages.map(
                (item, index) =>
                  item.role === "assistant" && (
                    <div key={index}>
                      <div className="message-item">
                        {" "}
                        {item.content.split("\n").map((line, lineIndex) => (
                          <span key={lineIndex}>
                            {line}
                            <br />
                          </span>
                        ))}
                      </div>
                      <br />
                    </div>
                  )
              )
            ) : (
              <div className="message-item">
                {introMessage.split("\n").map((line, lineIndex) => (
                  <span key={lineIndex}>
                    {line}
                    <br />
                  </span>
                ))}
              </div>
            )}
            {journeyStarted && messages.length > 2 && (
              <div
                style={{
                  position: "absolute",
                  height: "12%",
                  background: `
          
          linear-gradient(to bottom, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0) )
         
        `,
                  width: "35%",
                  top: "0",
                }}
              ></div>
            )}
          </div>

          {generating ? (
            <Logo
              style={{
                width: "12%",
                height: "5%",
                filter: "drop-shadow(0px 0px 12px black)",
                color: "black",
                animation: "rotate 2s linear infinite",
              }}
            ></Logo>
          ) : journeyStarted ? (
            <>
              <textarea
                value={messages.length <= 12 ? text : texts["en"]["expired"]}
                maxLength={350}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={texts["en"]["inputFieldHint"]}
                spellCheck={false}
                autoFocus={isMobile ? false : true}
                disabled={messages.length > 12}
                style={{
                  borderColor: error && "red",
                }}
              ></textarea>
              {messages.length > 12 && (
                <div
                  className="create-account"
                  onClick={() => navigate("/", { replace: true })}
                >
                  Create an account
                </div>
              )}
              <div
                className="help-button"
                onClick={() => askForOptions()}
                style={{ display: messages.length > 12 && "none" }}
              >
                ?
              </div>
              <div
                className="inventory"
                style={{
                  height: !showInventory && "0px",
                  width: !showInventory && "0px",
                  padding: !showInventory && "0px",
                }}
              >
                {Object.entries(items).map((item, index) => (
                  <div
                    key={index}
                    style={{ display: "flex", justifyContent: "center" }}
                  >
                    {item[0]} : {item[1].split(",")[0]}
                  </div>
                ))}
              </div>
              <div
                className="inventory-button"
                onClick={() => setShowInventory(!showInventory)}
              >
                <img src={Backpack} style={{ height: "80%" }} />
              </div>
            </>
          ) : (
            <div className="lang-select">
              <div className="lang-list">
                {languages.languages.map((item, index) => (
                  <div
                    key={index}
                    style={{
                      backgroundColor: languageIndex === index && "white",
                      color: languageIndex === index && "purple",
                    }}
                    onClick={() => {
                      setLanguageIndex(index);
                      introduction(languages.languages[index].short);
                    }}
                  >
                    {item.name}
                  </div>
                ))}
              </div>
              <div
                className="start-journey"
                onClick={() => {
                  startJourney();
                }}
              >
                {texts["en"]["startJourney"]}
              </div>
            </div>
          )}
        </>
      ) : (
        <div></div>
      )}
    </div>
  );
}

export default TestChat;
